import { useTranslation } from 'react-i18next';
import MailLineIcon from '../../../../atoms/icons/MailLineIcon';
import ButtonNew from '../../../../molecules/button/Button';
import styles from './CandidateInteraction.module.css';
import Text from '../../../../atoms/typography/Text';
import { FontWeightEnum } from '../../../../enums';
import MessageBox from '../../../../molecules/message-box/MessageBox';
import { HomeseekerApplication, Viewing } from '../../../../../api/homeseeker-application/findHomeSeekerApplicationById';
import SkeletonComponent from '../../../../../components/SkeletonComponent';
import PencilLineIcon from '../../../../atoms/icons/PencilLineIcon';
import CreateNoteModal from '../../../../../components/modals/CreateNoteModal';
import SendMessageModal from '../../../send-message-modal/SendMessageModal';
import moment from 'moment';
import Divider from '../../../../atoms/divider';
import { Badge } from '../../../../molecules/badge';

interface candidateInteractionProps {
    readonly homeseeker: HomeseekerApplication;
}

const CandidateInteraction = ({
    homeseeker,
}: candidateInteractionProps) => {
    const { t } = useTranslation();

    const renderBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return <Badge label={t('candidate.status.invitation.sent')} className={styles.pending} />
            case 'accepted':
                return <Badge label={t('candidate.status.invitation.confirmed')} className={styles.success} />
            case 'declined':
                return <Badge label={t('candidate.status.invitation.rejected')} className={styles.rejected} />
        }
    }

    const getViewings = (viewing: Viewing, key) => {
        const startDateTime = moment(viewing.startDateTime);
        const startTime = moment(viewing.startDateTime);
        const endTime = moment(viewing.endDateTime);

        return (
            <Text key={key} className={styles.viewingContainer}>
                {t('candidate.viewing.invitation', {
                    day: startDateTime.isValid() ? startDateTime.format('dddd DD-MM-YYYY') : '-',
                    startTime: startTime.isValid() ? startTime.format('HH:mm') : '-',
                    endTime: endTime.isValid() ? endTime.format('HH:mm') : '-'
                })}
                {renderBadge(viewing.status)}
            </Text>
        )
    }

    if (!homeseeker) {
        return (
            <SkeletonComponent />
        )
    }

    return (
        <div className={styles.candidateInteraction}>
            <div className={styles.viewings}>
                <div className={styles.header}>
                    <Text
                        weight={FontWeightEnum.Bold}
                    >
                        {t('candidate.table.title.viewingTitle')}
                    </Text>
                </div>
                <div className={styles.viewing}>
                    {homeseeker.viewings.map(getViewings)}
                </div>
            </div>
            <Divider />
            <div className={styles.messages}>
                <div className={styles.header}>
                    <Text
                        weight={FontWeightEnum.Bold}
                    >
                        {t('candidate.popover.title.message')}
                    </Text>
                    <ButtonNew
                        iconBefore={<MailLineIcon />}
                        onClick={() => { SendMessageModal.setOpen(true, homeseeker.id) }}
                    >
                        {t('candidate.action.initiateMessage')}
                    </ButtonNew>
                </div>
                <div className={styles.messagesContainer}>
                    {
                        homeseeker.messages.map((message, i) => {
                            return (
                                <MessageBox heading={message.details.subject} body={message.details.body} createdAt={message.createdAt} key={i} />
                            );
                        })
                    }
                </div>
            </div>
            <Divider />
            <div className={styles.notes}>
                <div className={styles.header}>
                    <Text
                        weight={FontWeightEnum.Bold}
                    >
                        {t('candidate.notes.title')}
                    </Text>
                    <ButtonNew
                        iconBefore={<PencilLineIcon />}
                        variant='secondary-gray'
                        className={styles.notesButton}
                        onClick={() => { CreateNoteModal.setOpen(true, homeseeker.id) }}
                    >
                        {t('candidate.notes.createNote')}
                    </ButtonNew>
                </div>
                <div className={styles.note}>
                    {
                        homeseeker.notes.map((note, i) => {
                            return (
                                <MessageBox body={note.note} createdAt={note.created_at} key={i} />
                            )
                        })
                    }
                </div>
            </div>
        </div>
    )
}

export default CandidateInteraction;
